/* The favicon monster peeking out from behind things, blinking now and then: from behind the sign-in card with its eye following
   the pointer, and from behind Fiona while she grades, scanning the ground. peeker() is the head from public/favicon.svg with the eye
   pulled out as .eye and its pupil as .pupil. This only moves the pupil of a pointer-following head and toggles .blink on the eye;
   the placement, blink and ground scan live in styles.css (.peeker). Reduced motion skips the easing and the blinking but the eye
   still looks. */
export const peeker = (look: "pointer" | "ground"): string => `<svg class="peeker look-${look}" viewBox="0 0 64 64" aria-hidden="true">
<defs><linearGradient id="peeker-g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#003570"/><stop offset="1" stop-color="#007A9E"/></linearGradient></defs>
<g transform="translate(32 32) scale(1.153) translate(-32 -32.88)">
<path d="M17 22 11 6l15 10M47 22l6-16-15 10" fill="#00B2DD"/>
<g transform="translate(32 36) scale(.88) translate(-32 -32)">
<path d="M32 4l5 6 7-3 2 7 7 0-1 7 6 4-4 6 4 6-6 4 1 7-7 1-2 7-7-3-5 6-5-6-7 3-2-7-7-1 1-7-6-4 4-6-4-6 6-4-1-7 7 0 2-7 7 3z" fill="url(#peeker-g)"/>
<g class="eye"><circle cx="32" cy="29" r="11" fill="#fff"/><circle class="pupil" cx="33.5" cy="30" r="5.5" fill="#202020"/></g>
<path d="M22 43q10 6 20 0" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round"/>
<path d="M27 45.5l2 4 2-3.5zM33 46l2 3.5 2-4z" fill="#fff"/>
</g>
</g>
</svg>`;

const EYE = { x: 32, y: 29 }; // centre of the eye white, in the eye's own coordinates
const REACH = 4.5;            // how far the pupil can travel from the centre before it touches the rim
const NEAR = 60;              // pointer distance (same units) at which the pupil reaches the rim
const EASE = 0.3;             // share of the remaining distance covered each frame
const BLINK = [1500, 4500];   // random wait between blinks, in ms

export function mountPeeker(): void {
  const still = matchMedia("(prefers-reduced-motion: reduce)");
  let px = 0, py = 0, x = 0, y = 0, tx = 0, ty = 0, frame = 0, pupil: SVGCircleElement | null = null;

  const paint = (): void => {
    if (!pupil?.isConnected) return;
    pupil.setAttribute("cx", (EYE.x + x).toFixed(2));
    pupil.setAttribute("cy", (EYE.y + y).toFixed(2));
  };
  const step = (): void => {
    x += (tx - x) * EASE; y += (ty - y) * EASE;
    if (Math.abs(tx - x) + Math.abs(ty - y) < 0.05) { x = tx; y = ty; frame = 0; } else frame = requestAnimationFrame(step);
    paint();
  };
  const aim = (): void => {
    // The app re-renders with innerHTML, so look the pupil up again whenever the old one has left the page.
    if (!pupil?.isConnected) { pupil = document.querySelector<SVGCircleElement>(".peeker.look-pointer .pupil"); x = y = 0; }
    // The eye's parent rather than the eye, which squashes while it blinks.
    const g = pupil?.parentElement?.parentElement, m = g instanceof SVGGraphicsElement ? g.getScreenCTM() : null;
    if (!pupil || !m || !pupil.getClientRects().length) return;
    // Into the eye's own coordinates, so the card-side tilt and scale of the head are already accounted for.
    const p = new DOMPoint(px, py).matrixTransform(m.inverse());
    const dx = p.x - EYE.x, dy = p.y - EYE.y, d = Math.hypot(dx, dy);
    const r = d ? REACH * Math.min(1, d / NEAR) / d : 0;
    tx = dx * r; ty = dy * r;
    if (still.matches) { cancelAnimationFrame(frame); frame = 0; x = tx; y = ty; paint(); }
    else if (!frame) frame = requestAnimationFrame(step);
  };

  addEventListener("pointermove", e => { px = e.clientX; py = e.clientY; aim(); }, { passive: true });
  addEventListener("pointerdown", e => { px = e.clientX; py = e.clientY; aim(); }, { passive: true });

  const blink = (): void => {
    const eye = document.querySelector(".peeker .eye");
    if (eye && !still.matches && !document.hidden) {
      eye.classList.add("blink");
      eye.addEventListener("animationend", () => eye.classList.remove("blink"), { once: true });
    }
    setTimeout(blink, BLINK[0] + Math.random() * (BLINK[1] - BLINK[0]));
  };
  setTimeout(blink, BLINK[1]);
}
